<script setup>
import { defineProps, ref, computed, onMounted } from "vue";
import { dashboardAPI } from "../service/api.js";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Card from "primevue/card";
import Button from "primevue/button";
import Tag from "primevue/tag";
import InputText from "primevue/inputtext";
import { formatCurrency, formatDate } from "@/util/format";
import { useToast } from "primevue/usetoast";

const toast = useToast();

const props = defineProps({
  batchId: {
    type: String,
    required: true,
  },
  batch: {
    type: Object,
    default: null,
  },
});

const loading = ref(false);
const transactions = ref([]);
const batchData = ref(props.batch || null);

const filters = ref({
  global: { value: null, matchMode: "contains" },
  claim_number: { value: null, matchMode: "contains" },
  external_trans_id: { value: null, matchMode: "contains" },
  carepay_vendor_id: { value: null, matchMode: "contains" },
});

const totalAmount = computed(() => {
  return transactions.value.reduce(
    (sum, trans) => sum + (trans.payment_amount || 0),
    0
  );
});

const transactionCount = computed(() => {
  return transactions.value.length;
});

const getStatusSeverity = (status) => {
  const severityMap = {
    pending: "warn",
    processed: "success",
    failed: "danger",
    new: "info",
  };
  return severityMap[status?.toLowerCase()] || "secondary";
};

const fetchBatchDetails = async () => {
  if (!batchData.value && props.batchId) {
    try {
      loading.value = true;
      const response = await dashboardAPI.getTransactionBatchById(
        props.batchId
      );
      batchData.value = response.data;
    } catch (error) {
      console.error("Error fetching batch details:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch batch details",
        life: 3000,
      });
    }
  }
};

const fetchTransactions = async () => {
  try {
    loading.value = true;
    const response = await dashboardAPI.getTransactionsByBatchId(props.batchId);
    transactions.value = response.data.data || [];
  } catch (error) {
    console.error("Error fetching transactions:", error);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to fetch transactions",
      life: 3000,
    });
  } finally {
    loading.value = false;
  }
};

const exportToCSV = () => {
  const headers = [
    "Claim Number",
    "External Transaction ID",
    "Carepay Vendor ID",
    "Vendor Name",
    "Vendor Email",
    "Payment Amount",
    "Payment Type",
    "Invoice Date",
    "Release Date",
    "Confirmation Number",
  ];

  const csvContent = [
    headers.join(","),
    ...transactions.value.map((trans) =>
      [
        trans.claim_number || "",
        trans.external_trans_id || "",
        trans.carepay_vendor_id || "",
        trans.vendor_name || "",
        trans.vendor_email || "",
        trans.payment_amount || "0",
        trans.payment_type || "",
        trans.invoice_date ? formatDate(trans.invoice_date) : "",
        trans.release_date ? formatDate(trans.release_date) : "",
        trans.confirmation_number || "",
      ]
        .map((field) => `"${field}"`)
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `batch_${props.batchId}_transactions.csv`;
  a.click();
  window.URL.revokeObjectURL(url);

  toast.add({
    severity: "success",
    summary: "Success",
    detail: "Transactions exported successfully",
    life: 3000,
  });
};

onMounted(() => {
  fetchBatchDetails();
  fetchTransactions();
});
</script>

<template>
  <div class="batch-detail-view">
    <Card class="header-card">
      <template #title>
        <div class="header-title">
          <span>Transaction Batch Details</span>
          <Tag
            :severity="getStatusSeverity(batchData?.status)"
            :value="batchData?.status || 'Unknown'"
          />
        </div>
      </template>
      <template #content>
        <div class="batch-info-grid">
          <div class="info-item">
            <label>Payment File ID</label>
            <p>
              <a :href="`/batch/${batchData?.batch_id}`" class="batch-link">
                {{ batchData?.batch_id }}
              </a>
            </p>
          </div>
          <div class="info-item">
            <label>Payment ID</label>
            <p>
              <a
                :href="`/#/c/${batchData?.external_batch_id}`"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ batchData?.external_batch_id }}
              </a>
            </p>
          </div>
          <div class="info-item">
            <label>Vendor ID</label>
            <p>{{ batchData?.vendor_id || "N/A" }}</p>
          </div>
          <div class="info-item">
            <label>Total Amount</label>
            <p class="amount">
              {{ formatCurrency(batchData?.amount || totalAmount) }}
            </p>
          </div>
          <div class="info-item">
            <label>Employer</label>
            <p>{{ batchData?.employer_code || "N/A" }}</p>
          </div>
          <div class="info-item">
            <label>Register Date</label>
            <p>{{ batchData?.register_date || "N/A" }}</p>
          </div>
        </div>
      </template>
    </Card>

    <Card class="summary-card">
      <template #title>Summary</template>
      <template #content>
        <div class="summary-grid">
          <div class="summary-item">
            <i class="pi pi-file-o"></i>
            <div>
              <h3>{{ transactionCount }}</h3>
              <p>Total Transactions</p>
            </div>
          </div>
          <div class="summary-item">
            <i class="pi pi-dollar"></i>
            <div>
              <h3>{{ formatCurrency(totalAmount) }}</h3>
              <p>Total Amount</p>
            </div>
          </div>
          <div class="summary-item">
            <i class="pi pi-calculator"></i>
            <div>
              <h3>
                {{
                  transactionCount > 0
                    ? formatCurrency(totalAmount / transactionCount)
                    : "$0.00"
                }}
              </h3>
              <p>Average Transaction</p>
            </div>
          </div>
        </div>
      </template>
    </Card>

    <Card class="transactions-card">
      <template #title>
        <div class="transactions-header">
          <span>Transactions</span>
          <Button
            icon="pi pi-download"
            label="Export CSV"
            severity="secondary"
            outlined
            @click="exportToCSV"
            :disabled="transactions.length === 0"
          />
        </div>
      </template>
      <template #content>
        <DataTable
          :value="transactions"
          :loading="loading"
          :filters="filters"
          filterDisplay="row"
          paginator
          :rows="10"
          :rowsPerPageOptions="[10, 25, 50]"
          responsiveLayout="scroll"
        >
          <template #empty>No transactions found for this batch.</template>

          <Column
            field="claim_number"
            header="Claim Number"
            sortable
            :showFilterMenu="false"
          >
            <template #filter="{ filterModel }">
              <InputText
                v-model="filterModel.value"
                type="text"
                placeholder="Search claim number"
              />
            </template>
          </Column>

          <Column
            field="external_trans_id"
            header="External Trans ID"
            sortable
            :showFilterMenu="false"
          >
            <template #filter="{ filterModel }">
              <InputText
                v-model="filterModel.value"
                type="text"
                placeholder="Search trans ID"
              />
            </template>
          </Column>

          <Column
            field="carepay_vendor_id"
            header="Vendor ID"
            sortable
            :showFilterMenu="false"
          >
            <template #filter="{ filterModel }">
              <InputText
                v-model="filterModel.value"
                type="text"
                placeholder="Search vendor ID"
              />
            </template>
          </Column>

          <Column field="vendor_name" header="Vendor Name" sortable></Column>

          <Column field="vendor_email" header="Vendor Email"></Column>

          <Column field="payment_amount" header="Amount" sortable>
            <template #body="slotProps">
              <span class="amount">{{
                formatCurrency(slotProps.data.payment_amount)
              }}</span>
            </template>
          </Column>

          <Column field="payment_type" header="Payment Type"></Column>

          <Column field="invoice_date" header="Invoice Date" sortable>
            <template #body="slotProps">
              {{
                slotProps.data.invoice_date
                  ? formatDate(slotProps.data.invoice_date)
                  : "N/A"
              }}
            </template>
          </Column>

          <Column field="release_date" header="Release Date">
            <template #body="slotProps">
              {{
                slotProps.data.release_date
                  ? formatDate(slotProps.data.release_date)
                  : "N/A"
              }}
            </template>
          </Column>

          <Column field="dcn" header="DCN"></Column>

          <Column field="confirmation_number" header="Confirmation #"></Column>
        </DataTable>
      </template>
    </Card>
  </div>
</template>

<style scoped>
.batch-detail-view {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1rem;
}

.header-card {
  background: var(--p-surface-0);
}

.header-title {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 1.5rem;
  font-weight: 600;
}

.batch-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-item label {
  font-size: 0.875rem;
  color: var(--p-text-secondary);
  font-weight: 500;
}

.info-item p {
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
}

.amount {
  color: var(--p-primary-500);
  font-weight: bold;
}

.summary-card {
  background: var(--p-surface-0);
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: var(--p-surface-50);
  border-radius: var(--p-border-radius);
}

.summary-item i {
  font-size: 2rem;
  color: var(--p-primary-500);
}

.summary-item h3 {
  margin: 0;
  font-size: 1.5rem;
  color: var(--p-text-primary);
}

.summary-item p {
  margin: 0;
  font-size: 0.875rem;
  color: var(--p-text-secondary);
}

.transactions-card {
  background: var(--p-surface-0);
}

.transactions-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .batch-info-grid {
    grid-template-columns: 1fr;
  }

  .summary-grid {
    grid-template-columns: 1fr;
  }

  .transactions-header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
}

/* Loading state */
:deep(.p-datatable-loading-overlay) {
  background-color: rgba(255, 255, 255, 0.9);
}

/* Table hover effects */
:deep(.p-datatable-tbody > tr:hover) {
  background-color: var(--p-surface-50);
}

/* Filter input styling */
:deep(.p-column-filter-row input) {
  width: 100%;
}

/* Batch link styling */
.batch-link {
  color: var(--p-primary-500);
  text-decoration: none;
  font-weight: 600;
}

.batch-link:hover {
  text-decoration: underline;
}
</style>
